import { parseDepartmentHierarchy, normalizeDepartmentValue } from './department-utils';
import { debugLog } from './debug-log';

/**
 * Employee Mapping IndexedDB Storage
 * Handles CSV employee data storage and API key mapping using IndexedDB
 */

export interface EmployeeRecord {
  id?: string;                // Auto-generated ID
  employeeId?: string;        // 社員番号
  name?: string;              // 氏名
  nameKana?: string;          // 氏名カナ
  role?: string;              // 役職
  email: string;              // メールアドレス（キー）
  phone?: string;             // 社用携帯
  department?: string;        // 所属
  joinDate?: string;          // 入社日
  headquarter?: string;       // Derived headquarter
  departmentUnit?: string;    // Derived department-level unit
  departmentHierarchy?: string[]; // Derived hierarchy levels
  normalizedDepartment?: string;  // Normalized department label
  generalHeadquarters?: string[]; // Tokens that were ignored as 統括本部
  apiKeyId?: string;          // 紐付けられたAPI Key ID
  apiKeyIds?: string[];       // All explicitly mapped provider key IDs
  apiKeyName?: string;        // 紐付けられたAPI Key Name
  lastUpdated: string;        // 最終更新日時
  createdAt: string;          // 作成日時
}

export interface ImportHistory {
  id?: string;
  fileName: string;
  totalRecords: number;
  matchedRecords: number;
  updatedRecords: number;
  failedRecords: number;
  importedAt: string;
  importedBy?: string;
}

export interface MappingConfig {
  id: string;
  columnMappings: {
    csvColumn: string;
    targetField: keyof EmployeeRecord;
  }[];
  emailDomainAutoComplete: boolean;
  partialMatch: boolean;
  caseSensitive: boolean;
  lastUpdated: string;
}

const DB_NAME = 'UsageDashboardEmployeeDB';
const DB_VERSION = 2; // Keep version 2 to avoid downgrade error

function applyDepartmentDerivedFields(record: EmployeeRecord): EmployeeRecord {
  const cleanedDepartment = typeof record.department === 'string'
    ? normalizeDepartmentValue(record.department)
    : '';

  if (typeof record.department === 'string') {
    record.department = cleanedDepartment || undefined;
  }

  const hierarchy = parseDepartmentHierarchy(record.department);
  record.headquarter = hierarchy.headquarter;
  record.departmentUnit = hierarchy.department;
  record.departmentHierarchy = hierarchy.hierarchy;
  record.normalizedDepartment = hierarchy.normalized;
  record.generalHeadquarters = hierarchy.generalHeadquarters;

  return record;
}

function buildPersistableEmployee(base: EmployeeRecord): EmployeeRecord {
  const record: EmployeeRecord = { ...base };

  record.email = record.email.trim().toLowerCase();
  if (record.employeeId) record.employeeId = record.employeeId.trim();
  if (record.name) record.name = record.name.trim();
  if (record.nameKana) record.nameKana = record.nameKana.trim();
  if (record.role) record.role = record.role.trim();
  if (record.phone) record.phone = record.phone.trim();
  if (record.joinDate) record.joinDate = record.joinDate.trim();
  if (record.apiKeyIds) {
    record.apiKeyIds = Array.from(new Set(
      record.apiKeyIds.map(value => value.trim()).filter(Boolean)
    ));
  }

  if (record.id === undefined || record.id === null || record.id === '' || Number.isNaN(record.id)) {
    delete record.id;
  }

  applyDepartmentDerivedFields(record);
  return record;
}

function decorateEmployeeRecord(raw: EmployeeRecord | null | undefined): EmployeeRecord | null {
  if (!raw) {
    return null;
  }

  const record: EmployeeRecord = { ...raw };

  record.email = record.email.trim().toLowerCase();
  if (record.apiKeyIds) {
    record.apiKeyIds = Array.from(new Set(
      record.apiKeyIds.map(value => String(value).trim()).filter(Boolean)
    ));
  }
  applyDepartmentDerivedFields(record);

  return record;
}

class EmployeeIndexedDB {
  private db: IDBDatabase | null = null;

  /**
   * Initialize database connection
   */
  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        debugLog('IndexedDB initialized successfully');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create employees object store
        if (!db.objectStoreNames.contains('employees')) {
          const employeeStore = db.createObjectStore('employees', { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          employeeStore.createIndex('email', 'email', { unique: true });
          employeeStore.createIndex('apiKeyId', 'apiKeyId', { unique: false });
          employeeStore.createIndex('department', 'department', { unique: false });
          employeeStore.createIndex('role', 'role', { unique: false });
          debugLog('Created employees object store');
        }

        // Create import history store
        if (!db.objectStoreNames.contains('importHistory')) {
          const historyStore = db.createObjectStore('importHistory', { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          historyStore.createIndex('importedAt', 'importedAt', { unique: false });
          debugLog('Created importHistory object store');
        }

        // Create mapping config store
        if (!db.objectStoreNames.contains('mappingConfig')) {
          db.createObjectStore('mappingConfig', { keyPath: 'id' });
          debugLog('Created mappingConfig object store');
        }
      };
    });
  }

  /**
   * Ensure database is initialized
   */
  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init();
    }
    if (!this.db) {
      throw new Error('Failed to initialize database');
    }
    return this.db;
  }

  /**
   * Save or update employee record
   */
  async saveEmployee(employee: EmployeeRecord): Promise<string> {
    const db = await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['employees'], 'readwrite');
      const store = transaction.objectStore('employees');
      const emailIndex = store.index('email');
      const normalizedEmail = employee.email.trim().toLowerCase();

      const getRequest = emailIndex.get(normalizedEmail);
      
      getRequest.onsuccess = () => {
        const existing = getRequest.result as EmployeeRecord | undefined;
        const now = new Date().toISOString();

        const baseRecord: EmployeeRecord = {
          ...(existing ?? {}),
          ...employee,
          email: normalizedEmail,
          createdAt: employee.createdAt || now,
          lastUpdated: now
        };

        if (existing && existing.id !== undefined && existing.id !== null) {
          baseRecord.id = existing.id;
          baseRecord.createdAt = existing.createdAt || baseRecord.createdAt;
          // CSV imports do not carry API-key mappings. Preserve explicit
          // mappings unless a caller intentionally provides replacements.
          if (employee.apiKeyId === undefined) baseRecord.apiKeyId = existing.apiKeyId;
          if (employee.apiKeyIds === undefined) baseRecord.apiKeyIds = existing.apiKeyIds;
          if (employee.apiKeyName === undefined) baseRecord.apiKeyName = existing.apiKeyName;
        } else {
          delete baseRecord.id;
        }

        const record = buildPersistableEmployee(baseRecord);

        const request = existing
          ? store.put(record)
          : store.add(record);
        
        request.onsuccess = () => {
          resolve(String(request.result));
        };
        
        request.onerror = () => {
          reject(request.error);
        };
      };
      
      getRequest.onerror = () => {
        reject(getRequest.error);
      };
    });
  }

  /**
   * Bulk save employees
   */
  async saveEmployees(employees: EmployeeRecord[]): Promise<{ 
    saved: number; 
    failed: Array<{ email: string; error: string }> 
  }> {
    const results = { saved: 0, failed: [] as Array<{ email: string; error: string }> };

    for (const employee of employees) {
      try {
        const normalizedEmail = employee.email?.trim().toLowerCase();
        if (!normalizedEmail) {
          results.failed.push({
            email: employee.email,
            error: 'Missing email'
          });
          continue;
        }

        const employeeForSave: EmployeeRecord = {
          ...employee,
          id: undefined,
          email: normalizedEmail
        } as EmployeeRecord;

        await this.saveEmployee(employeeForSave);
        results.saved++;
      } catch (error) {
        results.failed.push({ 
          email: employee.email, 
          error: error instanceof Error ? error.message : 'Unknown error' 
        });
      }
    }
    
    return results;
  }

  /**
   * Get employee by email
   */
  async getEmployeeByEmail(email: string): Promise<EmployeeRecord | null> {
    const db = await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['employees'], 'readonly');
      const store = transaction.objectStore('employees');
      const index = store.index('email');
      const normalizedEmail = email.trim().toLowerCase();
      const request = index.get(normalizedEmail);
      
      request.onsuccess = () => {
        resolve(decorateEmployeeRecord(request.result));
      };
      
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Get all employees
   */
  async getAllEmployees(): Promise<EmployeeRecord[]> {
    const db = await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['employees'], 'readonly');
      const store = transaction.objectStore('employees');
      const request = store.getAll();
      
      request.onsuccess = () => {
        const records = (request.result || [])
          .map(record => decorateEmployeeRecord(record))
          .filter((record): record is EmployeeRecord => record !== null);
        resolve(records);
      };
      
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Get employees by department
   */
  async getEmployeesByDepartment(department: string): Promise<EmployeeRecord[]> {
    const db = await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['employees'], 'readonly');
      const store = transaction.objectStore('employees');
      const index = store.index('department');
      const normalizedDepartment = normalizeDepartmentValue(department);
      const request = index.getAll(normalizedDepartment || department);
      
      request.onsuccess = () => {
        const records = (request.result || [])
          .map(record => decorateEmployeeRecord(record))
          .filter((record): record is EmployeeRecord => record !== null);
        resolve(records);
      };
      
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Search employees with partial email match
   */
  async searchEmployeesByEmail(emailPattern: string, caseSensitive = false): Promise<EmployeeRecord[]> {
    const employees = await this.getAllEmployees();
    const pattern = caseSensitive ? emailPattern : emailPattern.toLowerCase();
    
    return employees.filter(emp => {
      const email = caseSensitive ? emp.email : emp.email.toLowerCase();
      return email.includes(pattern);
    });
  }

  /**
   * Map employee to API key
   */
  async mapEmployeeToApiKey(email: string, apiKeyId: string, apiKeyName: string): Promise<boolean> {
    const employee = await this.getEmployeeByEmail(email);
    if (!employee) {
      return false;
    }
    
    employee.apiKeyIds = Array.from(new Set([
      ...(employee.apiKeyIds ?? []),
      ...(employee.apiKeyId ? [employee.apiKeyId] : []),
      apiKeyId
    ]));
    // Keep the most recent mapping in the legacy field for existing UI/indexes.
    employee.apiKeyId = apiKeyId;
    employee.apiKeyName = apiKeyName;
    
    try {
      await this.saveEmployee(employee);
      return true;
    } catch (error) {
      console.error('Failed to map employee to API key:', error);
      return false;
    }
  }

  /**
   * Get employees mapped to API key
   */
  async getEmployeesByApiKey(apiKeyId: string): Promise<EmployeeRecord[]> {
    const db = await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['employees'], 'readonly');
      const store = transaction.objectStore('employees');
      const index = store.index('apiKeyId');
      const request = index.getAll(apiKeyId);
      
      request.onsuccess = () => {
        const records = (request.result || [])
          .map(record => decorateEmployeeRecord(record))
          .filter((record): record is EmployeeRecord => record !== null);
        resolve(records);
      };
      
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Delete employee record
   */
  async deleteEmployee(email: string): Promise<boolean> {
    const db = await this.ensureDB();
    const employee = await this.getEmployeeByEmail(email);
    
    if (!employee || !employee.id) {
      return false;
    }
    
    return new Promise((resolve, _reject) => {
      const transaction = db.transaction(['employees'], 'readwrite');
      const store = transaction.objectStore('employees');
      const request = store.delete(employee.id);
      
      request.onsuccess = () => {
        resolve(true);
      };
      
      request.onerror = () => {
        console.error('Failed to delete employee:', request.error);
        resolve(false);
      };
    });
  }

  /**
   * Clear all employee data
   */
  async clearAllEmployees(): Promise<boolean> {
    const db = await this.ensureDB();
    
    return new Promise((resolve) => {
      const transaction = db.transaction(['employees'], 'readwrite');
      const store = transaction.objectStore('employees');
      const request = store.clear();
      
      request.onsuccess = () => {
        resolve(true);
      };
      
      request.onerror = () => {
        console.error('Failed to clear employees:', request.error);
        resolve(false);
      };
    });
  }

  /**
   * Save import history
   */
  async saveImportHistory(history: ImportHistory): Promise<void> {
    const db = await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['importHistory'], 'readwrite');
      const store = transaction.objectStore('importHistory');
      
      history.importedAt = new Date().toISOString();
      const request = store.add(history);
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Get import history
   */
  async getImportHistory(limit = 10): Promise<ImportHistory[]> {
    const db = await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['importHistory'], 'readonly');
      const store = transaction.objectStore('importHistory');
      const index = store.index('importedAt');
      
      const results: ImportHistory[] = [];
      let count = 0;
      
      const request = index.openCursor(null, 'prev'); // Sort by date descending
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor && count < limit) {
          results.push(cursor.value);
          count++;
          cursor.continue();
        } else {
          resolve(results);
        }
      };
      
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Save mapping configuration
   */
  async saveMappingConfig(config: MappingConfig): Promise<void> {
    const db = await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['mappingConfig'], 'readwrite');
      const store = transaction.objectStore('mappingConfig');
      
      config.lastUpdated = new Date().toISOString();
      const request = store.put(config);
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Get mapping configuration
   */
  async getMappingConfig(id = 'default'): Promise<MappingConfig | null> {
    const db = await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['mappingConfig'], 'readonly');
      const store = transaction.objectStore('mappingConfig');
      const request = store.get(id);
      
      request.onsuccess = () => {
        resolve(request.result || null);
      };
      
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Get database statistics
   */
  async getStatistics(): Promise<{
    totalEmployees: number;
    mappedEmployees: number;
    departments: string[];
    roles: string[];
    lastImport?: ImportHistory;
  }> {
    const employees = await this.getAllEmployees();
    const history = await this.getImportHistory(1);
    
    const departments = new Set<string>();
    const roles = new Set<string>();
    let mappedCount = 0;
    
    employees.forEach(emp => {
      if (emp.apiKeyId) mappedCount++;
      if (emp.normalizedDepartment) {
        departments.add(emp.normalizedDepartment);
      } else if (emp.headquarter) {
        departments.add(emp.headquarter);
      }
      if (emp.role) roles.add(emp.role);
    });
    
    return {
      totalEmployees: employees.length,
      mappedEmployees: mappedCount,
      departments: Array.from(departments).sort(),
      roles: Array.from(roles).sort(),
      lastImport: history[0]
    };
  }

  /**
   * Close database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

// Export singleton instance
export const employeeDB = new EmployeeIndexedDB();

// Helper function to auto-map employees to API keys
export async function autoMapEmployeesToApiKeys(
  apiKeys: Array<{ id: string; name: string }>,
  config?: { 
    domainAutoComplete?: boolean; 
    partialMatch?: boolean; 
    caseSensitive?: boolean; 
  }
): Promise<{
  matched: number;
  unmatched: string[];
}> {
  const { 
    domainAutoComplete = true, 
    partialMatch = true, 
    caseSensitive = false 
  } = config || {};
  
  const results = { matched: 0, unmatched: [] as string[] };
  
  for (const apiKey of apiKeys) {

    
    // Try exact match first
    let employee = await employeeDB.getEmployeeByEmail(apiKey.name);
    
    // Try with domain auto-complete
    if (!employee && domainAutoComplete && !apiKey.name.includes('@')) {
      const emailWithDomain = `${apiKey.name}@system-exe.jp`;
      employee = await employeeDB.getEmployeeByEmail(emailWithDomain);
      
      if (!employee) {
        // Try with alternative domains
        const alternativeDomains = ['@system-exe.co.jp', '@on.system-exe.jp', '@system-exe.com.vn'];
        for (const domain of alternativeDomains) {
          employee = await employeeDB.getEmployeeByEmail(apiKey.name + domain);
          if (employee) break;
        }
      }
    }
    
    // Try partial match
    if (!employee && partialMatch) {
      const employees = await employeeDB.searchEmployeesByEmail(apiKey.name, caseSensitive);
      if (employees.length === 1) {
        employee = employees[0];
      }
    }
    
    if (employee) {
      const mapped = await employeeDB.mapEmployeeToApiKey(
        employee.email, 
        apiKey.id, 
        apiKey.name
      );
      if (mapped) {
        results.matched++;
      } else {
        results.unmatched.push(apiKey.name);
      }
    } else {
      results.unmatched.push(apiKey.name);
    }
  }
  
  return results;
}
